"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";
import { getRankingGroupsClient, getRankingHospitalsClient } from "@/entities/search-ranking/api/search-ranking.client";
import { isRegionAllSelectionByIds } from "@/features/search-result/lib/regionAllOption";
import { Region } from "@/shared/types/type/search";
import { mapRankingApiToItem } from "@/widgets/search-ranking/lib/mapRankingApiToItem";
// 그룹 판정·queryKey는 서버 프리페치(page)와 공유하는 계약 — rankingQuery 한곳에서만 수정
import { findRankingGroupId, rankingQueryKeys, resolveRankingSelection } from "@/widgets/search-ranking/lib/rankingQuery";
interface UseRankingHospitalsParams {
  regionList: Region[];
}
export const useRankingHospitals = t0 => {
  const $ = _c(21);
  const {
    regionList
  } = t0;
  const params = useSearchParams();
  const {
    regionIds,
    treatmentCategoryId
  } = resolveRankingSelection(params?.get("region"), params?.get("treatment"));
  const regionAll = isRegionAllSelectionByIds(regionList, regionIds);
  const regionCategoryId = regionIds[0];
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: rankingQueryKeys.groups,
      queryFn: getRankingGroupsClient,
      select: _temp
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const rankingGroupsQuery = useQuery(t1);
  const hospitalRankingGroupId = findRankingGroupId(rankingGroupsQuery.data, regionCategoryId, treatmentCategoryId);
  const query = useQuery({
    queryKey: rankingQueryKeys.hospitals(hospitalRankingGroupId ?? "none", regionAll),
    queryFn: () => {
      if (hospitalRankingGroupId == null) {
        throw new Error("Ranking group not found");
      }
      return getRankingHospitalsClient({
        hospitalRankingGroupId
      });
    },
    enabled: hospitalRankingGroupId != null,
    select: _temp3
  });
  let t2;
  if ($[1] !== query.data?.items) {
    t2 = query.data?.items ?? [];
    $[1] = query.data?.items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const items = t2;
  const totalReviewCount = query.data?.totalReviewCount ?? 0;
  const rankingGroups = rankingGroupsQuery.data;
  let regionIdSet;
  let treatmentIdSet;
  if ($[3] !== rankingGroups) {
    regionIdSet = new Set();
    treatmentIdSet = new Set();
    for (const group of rankingGroups ?? []) {
      regionIdSet.add(group.regionCategoryId);
      treatmentIdSet.add(group.treatmentCategoryId);
    }
    $[3] = rankingGroups;
    $[4] = regionIdSet;
    $[5] = treatmentIdSet;
  } else {
    regionIdSet = $[4];
    treatmentIdSet = $[5];
  }
  let t3;
  if ($[6] !== regionIdSet || $[7] !== treatmentIdSet) {
    t3 = {
      regionIds: regionIdSet,
      treatmentIds: treatmentIdSet
    };
    $[6] = regionIdSet;
    $[7] = treatmentIdSet;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const supported = t3;
  const isGroupsReady = rankingGroups != null;
  const isUnsupportedRegion = isGroupsReady && !supported.regionIds.has(regionCategoryId);
  const isUnsupportedTreatment = isGroupsReady && !supported.treatmentIds.has(treatmentCategoryId);
  const t4 = rankingGroupsQuery.isLoading || query.isLoading;
  const t5 = rankingGroupsQuery.isLoading || hospitalRankingGroupId != null;
  const t6 = isUnsupportedRegion || isUnsupportedTreatment;
  let t7;
  if ($[9] !== isUnsupportedRegion || $[10] !== isUnsupportedTreatment || $[11] !== items || $[12] !== regionCategoryId || $[13] !== supported.regionIds || $[14] !== supported.treatmentIds || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== totalReviewCount || $[19] !== treatmentCategoryId) {
    t7 = {
      items,
      totalReviewCount,
      fetchNextPage: _temp4,
      hasNextPage: false,
      isFetchingNextPage: false,
      isLoading: t4,
      isQueryEnabled: t5,
      isUnsupportedSelection: t6,
      isUnsupportedRegion,
      isUnsupportedTreatment,
      supportedRegionIds: supported.regionIds,
      supportedTreatmentIds: supported.treatmentIds,
      selectedRegionCategoryId: regionCategoryId,
      selectedTreatmentCategoryId: treatmentCategoryId
    };
    $[9] = isUnsupportedRegion;
    $[10] = isUnsupportedTreatment;
    $[11] = items;
    $[12] = regionCategoryId;
    $[13] = supported.regionIds;
    $[14] = supported.treatmentIds;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = totalReviewCount;
    $[19] = treatmentCategoryId;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
function _temp(data) {
  return data.result;
}
function _temp2(item) {
  return mapRankingApiToItem(item);
}
function _temp3(data_0) {
  return {
    items: data_0.result.hospitals.map(_temp2),
    totalReviewCount: data_0.result.hospitalReviewCount
  };
}
function _temp4() {}